import { ReactNode, useEffect, useState } from 'react';

import { REFRIGERATOR_OPTIONS } from '~/core/enums';
import { SectionTypes } from '~/core/enums/section-type-enum';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { ApplianceModel, Filter } from '~/core/models';
import { createTypeSafeContext } from '~/core/utils';
import {
  initializeFiltersOptions,
  updateFiltersOptions,
} from '~/core/utils/sortAndFilter';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectInStoreRefrigeratorsUrl,
  selectSelectedRefrigerator,
  updateInStoreRefrigeratorsUrl,
  updateSelectedRefrigerator,
} from '~/redux/slices/refrigerator';

type RefrigeratorFilters = Record<string, Filter>;

interface RefrigeratorContextValues {
  suppliedRefrigerators: ApplianceModel[];
  updateSuppliedRefrigerators: (refrigerators: ApplianceModel[]) => void;
  browsedRefrigerators: ApplianceModel[];
  updateBrowsedRefrigerators: (refrigerators: ApplianceModel[]) => void;
  selectedOption?: REFRIGERATOR_OPTIONS;
  updateSelectedOption: (newOption?: REFRIGERATOR_OPTIONS) => void;
  filteredRefrigerators: ApplianceModel[];
  isFilterExpanded: boolean;
  updateFilteredRefrigerators: (refrigerators: ApplianceModel[]) => void;
  refrigeratorFilters: RefrigeratorFilters;
  updateRefrigeratorFilters: (
    refrigeratorFilters: Record<string, Filter>
  ) => void;
  updateFiltersExpandState: (value: boolean) => void;
  resetRefrigerators: () => void;
  selectedRefrigerator?: ApplianceModel;
  inStoreRefrigeratorsUrl?: string;
  updateSelectedRefrigeratorByUser: (value: ApplianceModel) => void;
}

export const [useRefrigeratorContext, RefrigeratorContext] =
  createTypeSafeContext<RefrigeratorContextValues>();

export const RefrigeratorContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<
    REFRIGERATOR_OPTIONS | undefined
  >();
  const [filteredRefrigerators, setFilteredRefrigerators] = useState<
    ApplianceModel[]
  >([]);
  const [isFilterExpanded, setFilterExpanded] = useState(false);
  const [refrigeratorFilters, setRefrigeratorFilters] =
    useState<RefrigeratorFilters>({});
  const [browsedRefrigerators, setBrowsedRefrigerators] = useState<
    ApplianceModel[]
  >([]);
  const [suppliedRefrigerators, setSuppliedRefrigerators] = useState<
    ApplianceModel[]
  >([]);

  const selectedRefrigerator = useAppSelector(selectSelectedRefrigerator);
  const inStoreRefrigeratorsUrl = useAppSelector(selectInStoreRefrigeratorsUrl);

  const dispatch = useAppDispatch();
  const { getInStoreApplianceUrl } = useRetailerSettings();

  useEffect(() => {
    const url = getInStoreApplianceUrl(SectionTypes.refrigerator);

    url && dispatch(updateInStoreRefrigeratorsUrl(url));
  }, [dispatch]);

  const updateSelectedRefrigeratorByUser = (value: ApplianceModel) => {
    dispatch(updateSelectedRefrigerator(value));
  };

  const updateBrowsedRefrigerators = (refrigerators: ApplianceModel[]) => {
    setBrowsedRefrigerators(refrigerators);
    if (refrigerators.length > 0) {
      setFilteredRefrigerators(refrigerators);
      initializeFiltersOptions(
        refrigerators,
        setRefrigeratorFilters,
        refrigeratorFilters
      );
    }
  };

  const updateSuppliedRefrigerators = (refrigerators: ApplianceModel[]) => {
    setSuppliedRefrigerators(refrigerators);
  };

  const updateSelectedOption = (newOption?: REFRIGERATOR_OPTIONS) => {
    setSelectedOption(newOption);
  };

  const updateFilteredRefrigerators = (
    filteredRefrigerators: ApplianceModel[]
  ) => {
    setFilteredRefrigerators(filteredRefrigerators);
    updateFiltersOptions(refrigeratorFilters, browsedRefrigerators);
  };

  const updateRefrigeratorFilters = (
    refrigeratorFilters: Record<string, Filter>
  ) => {
    setRefrigeratorFilters(refrigeratorFilters);
  };

  // expand/collapse the filters
  const updateFiltersExpandState = (value: boolean) => {
    setFilterExpanded(value);
  };

  const resetRefrigerators = () => {
    switch (selectedOption) {
      case REFRIGERATOR_OPTIONS.BROWSE_REFRIGERATORS:
        setBrowsedRefrigerators([]);
        setFilteredRefrigerators([]);
        break;
      case REFRIGERATOR_OPTIONS.SUPPLY_REFRIGERATORS:
        setSuppliedRefrigerators([]);
        break;
      default:
        break;
    }
    setSelectedOption(undefined);
  };

  const refrigeratorContextValue: RefrigeratorContextValues = {
    suppliedRefrigerators,
    updateSuppliedRefrigerators,
    browsedRefrigerators,
    updateBrowsedRefrigerators,
    selectedOption,
    updateSelectedOption,
    filteredRefrigerators,
    isFilterExpanded,
    updateFilteredRefrigerators,
    refrigeratorFilters,
    updateRefrigeratorFilters,
    updateFiltersExpandState,
    resetRefrigerators,
    selectedRefrigerator,
    inStoreRefrigeratorsUrl,
    updateSelectedRefrigeratorByUser,
  };

  return (
    <RefrigeratorContext.Provider value={refrigeratorContextValue}>
      {children}
    </RefrigeratorContext.Provider>
  );
};
